import { useState } from 'react';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { PageHeader } from '../../components/common';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Avatar,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Chip,
  Stack,
  Divider,
  StepConnector,
  stepConnectorClasses,
  StepIconProps,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Business,
  Save,
  Cancel,
  CloudUpload,
  ArrowBack,
  ArrowForward,
  CheckCircle,
  LocationOn,
  Language,
  Phone,
  Email,
  Extension,
  Person,
  Check,
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  Public,
  ContactMail,
  AdminPanelSettings,
  Summarize,
  Receipt,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { superAdminService, CreateCountyData } from '../../services/superadmin.service';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { colors } from '../../theme/theme';
import {
  BillingConfigForm,
  BillingFormValue,
  emptyBilling,
  serializeBilling,
  validateBilling,
} from '../../components/billing/BillingConfigForm';

const steps = [
  { label: 'County Details', icon: Business },
  { label: 'Contact Info', icon: ContactMail },
  { label: 'Modules', icon: Extension },
  { label: 'Touchpoints', icon: Language },
  { label: 'Admin Account', icon: AdminPanelSettings },
  { label: 'Billing', icon: Receipt },
  { label: 'Review', icon: Summarize },
];

const TOUCHPOINT_OPTIONS: Array<{ key: 'portal' | 'mobile' | 'whatsapp'; title: string; description: string }> = [
  { key: 'portal', title: 'Citizen Portal', description: 'Web portal where citizens browse services and pay bills' },
  { key: 'mobile', title: 'Mobile App', description: 'Branded mobile app (iOS / Android) for citizens on the go' },
  { key: 'whatsapp', title: 'WhatsApp', description: 'Conversational access via the county WhatsApp Business number' },
];

// Portal multi-tenancy: each onboarded county gets <subdomain>.<base domain>.
// Must mirror the backend's County::RESERVED_SUBDOMAINS + County::subdomainRules().
const PORTAL_BASE_DOMAIN = process.env.REACT_APP_PORTAL_BASE_DOMAIN || 'kauntee.com';
const RESERVED_SUBDOMAINS = [
  'api', 'admin', 'portal', 'payments', 'www', 'mail', 'app',
  'staging', 'dev', 'test', 'smtp', 'ftp', 'ns1', 'ns2', 'cdn',
  'static', 'assets', 'docs',
];
const SUBDOMAIN_REGEX = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
const slugifySubdomain = (name: string) =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);

// Custom Stepper Connector
const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: `linear-gradient(95deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: `linear-gradient(95deg, ${colors.success.main} 0%, ${colors.success.dark} 100%)`,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: colors.grey[200],
    borderRadius: 1,
  },
}));

// Custom Step Icon
const ColorlibStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
  backgroundColor: colors.grey[200],
  zIndex: 1,
  color: '#fff',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow: '0 4px 10px 0 rgba(0,0,0,.15)',
  ...(ownerState.active && {
    backgroundImage: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
    boxShadow: `0 4px 15px 0 ${colors.primary.main}80`,
  }),
  ...(ownerState.completed && {
    backgroundImage: `linear-gradient(135deg, ${colors.success.main} 0%, ${colors.success.dark} 100%)`,
  }),
}));

function ColorlibStepIcon(props: StepIconProps) {
  const { active, completed, className } = props;
  const icons: { [index: string]: React.ReactElement } = {
    1: <Business />,
    2: <ContactMail />,
    3: <Extension />,
    4: <AdminPanelSettings />,
    5: <Summarize />,
  };

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
      {completed ? <Check /> : icons[String(props.icon)]}
    </ColorlibStepIconRoot>
  );
}

export const CreateCountyPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<{
    county_id: number | '';
    subdomain: string;
    office_location: string;
    website: string;
    contacts: Record<number, string>;
    module_types: number[];
    touchpoints: Array<'portal' | 'mobile' | 'whatsapp'>;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  }>({
    county_id: '',
    subdomain: '',
    office_location: '',
    website: '',
    contacts: {},
    module_types: [],
    touchpoints: ['portal', 'mobile', 'whatsapp'],
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });
  const [logo, setLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});
  const [billing, setBilling] = useState<BillingFormValue>(emptyBilling());

  // Fetch form data
  const { data: countiesData } = useQuery({
    queryKey: ['all-counties'],
    queryFn: () => superAdminService.getAllCounties(),
  });

  const { data: contactsData } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => superAdminService.getContacts(),
  });

  const { data: moduleTypesData } = useQuery({
    queryKey: ['all-module-types'],
    queryFn: () => superAdminService.getAllModuleTypes(),
  });

  const counties = countiesData?.data || [];
  const contacts = contactsData?.data || [];
  const moduleTypes = moduleTypesData?.data || [];

  const createCounty = useMutation({
    mutationFn: (data: CreateCountyData) => superAdminService.createCounty(data),
    onSuccess: (response: any) => {
      const portalUrl = response?.data?.portal_url;
      showSnackbar(
        portalUrl
          ? `County onboarded successfully! Portal is live at ${portalUrl}. Admin credentials sent via email.`
          : 'County onboarded successfully! Admin credentials sent via email.',
        'success'
      );
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'counties'] });
      navigate('/superadmin/counties');
    },
    onError: (err: any) => {
      const errorData = err.response?.data;

      // Check if there are validation errors
      if (errorData?.errors && typeof errorData.errors === 'object') {
        setValidationErrors(errorData.errors);

        // Build a comprehensive error message
        const errorMessages = Object.entries(errorData.errors)
          .map(([field, messages]) => {
            const fieldName = field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
            return `${fieldName}: ${(messages as string[]).join(', ')}`;
          })
          .join('\n');

        setError(errorMessages);
        showSnackbar(errorData.message || 'Validation failed', 'error');

        // Scroll to first error field and navigate to appropriate step
        const errorFields = Object.keys(errorData.errors);
        if (errorFields.includes('county_id') || errorFields.includes('subdomain') || errorFields.includes('office_location') || errorFields.includes('website')) {
          setActiveStep(0); // County Details
        } else if (errorFields.some(f => f.startsWith('contacts'))) {
          setActiveStep(1); // Contact Info
        } else if (errorFields.includes('module_types')) {
          setActiveStep(2); // Modules
        } else if (errorFields.some(f => f.startsWith('touchpoints'))) {
          setActiveStep(3); // Touchpoints
        } else if (errorFields.includes('first_name') || errorFields.includes('last_name') || errorFields.includes('email') || errorFields.includes('phone')) {
          setActiveStep(4); // Admin Account
        } else if (errorFields.some(f => f.startsWith('billing'))) {
          setActiveStep(5); // Billing
        }

        window.scrollTo(0, 0);
      } else {
        setError(errorData?.message || err.message || 'Failed to create county');
        showSnackbar('Failed to create county', 'error');
      }
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: any) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactChange = (contactId: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      contacts: { ...prev.contacts, [contactId]: value },
    }));
  };

  const handleModuleToggle = (moduleId: number) => {
    setFormData((prev) => ({
      ...prev,
      module_types: prev.module_types.includes(moduleId)
        ? prev.module_types.filter((id) => id !== moduleId)
        : [...prev.module_types, moduleId],
    }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (2MB)
      if (file.size > 2 * 1024 * 1024) {
        setError('Logo file size must be less than 2MB');
        return;
      }
      setLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const getContactIcon = (contactName: string) => {
    const name = contactName.toLowerCase();
    if (name.includes('email')) return <Email />;
    if (name.includes('phone') || name.includes('mobile') || name.includes('tel')) return <Phone />;
    if (name.includes('facebook')) return <Facebook />;
    if (name.includes('twitter') || name.includes('x')) return <Twitter />;
    if (name.includes('instagram')) return <Instagram />;
    if (name.includes('linkedin')) return <LinkedIn />;
    if (name.includes('website') || name.includes('web')) return <Public />;
    return <ContactMail />;
  };

  const getContactPrefix = (contactName: string) => {
    const name = contactName.toLowerCase();
    if (name.includes('facebook')) return 'https://facebook.com/';
    if (name.includes('twitter') || name.includes('x')) return 'https://twitter.com/';
    if (name.includes('instagram')) return 'https://instagram.com/';
    if (name.includes('linkedin')) return 'https://linkedin.com/';
    return null;
  };

  const getContactPlaceholder = (contactName: string) => {
    const name = contactName.toLowerCase();
    if (name.includes('facebook')) return 'username or page-name';
    if (name.includes('twitter') || name.includes('x')) return 'username';
    if (name.includes('instagram')) return 'username';
    if (name.includes('linkedin')) return 'company/username';
    if (name.includes('email')) return 'contact@county.go.ke';
    if (name.includes('phone') || name.includes('mobile') || name.includes('tel')) return '+254 700 000 000';
    return `Enter ${contactName.toLowerCase()}`;
  };

  const formatContactValue = (contactId: number, value: string, contactName: string) => {
    const prefix = getContactPrefix(contactName);
    if (prefix && value && !value.startsWith('http')) {
      // Remove prefix if user accidentally typed it
      const cleanValue = value.replace(prefix, '');
      return cleanValue;
    }
    return value;
  };

  const handleContactChangeWithPrefix = (contactId: number, value: string, contactName: string) => {
    const formattedValue = formatContactValue(contactId, value, contactName);
    handleContactChange(contactId, formattedValue);
  };

  const validateStep = () => {
    switch (activeStep) {
      case 0: // County Details
        if (!formData.county_id) {
          setError('Please select a county');
          return false;
        }
        if (formData.subdomain) {
          if (!SUBDOMAIN_REGEX.test(formData.subdomain)) {
            setError('Subdomain may only contain lowercase letters, numbers and hyphens (no leading/trailing hyphen)');
            return false;
          }
          if (RESERVED_SUBDOMAINS.includes(formData.subdomain)) {
            setError(`"${formData.subdomain}" is a reserved subdomain — please choose another`);
            return false;
          }
        }
        break;
      case 2: // Modules
        if (formData.module_types.length === 0) {
          setError('Please select at least one module');
          return false;
        }
        break;
      case 3: // Touchpoints
        if (formData.touchpoints.length === 0) {
          setError('Select at least one touchpoint so citizens can reach the county');
          return false;
        }
        break;
      case 4: // Admin Account
        if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
          setError('All admin account fields are required');
          return false;
        }
        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          setError('Please enter a valid email address');
          return false;
        }
        break;
      case 5: // Billing
        const billingError = validateBilling(billing);
        if (billingError) {
          setError(billingError);
          return false;
        }
        break;
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      setActiveStep((prev) => prev + 1);
      setError('');
      setValidationErrors({});
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError('');
    setValidationErrors({});
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (formData.county_id === '') {
      setError('County is required');
      return;
    }

    // Build full URLs for social media contacts
    const processedContacts: Record<number, string> = {};
    contacts.forEach((contact: any) => {
      const value = formData.contacts[contact.id];
      if (value) {
        const prefix = getContactPrefix(contact.name);
        processedContacts[contact.id] = prefix ? `${prefix}${value}` : value;
      }
    });

    const submitData: CreateCountyData = {
      county_id: formData.county_id as number,
      ...(formData.subdomain && { subdomain: formData.subdomain }),
      office_location: formData.office_location,
      website: formData.website,
      contacts: processedContacts,
      module_types: formData.module_types,
      touchpoints: formData.touchpoints,
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      phone: formData.phone,
      billing: serializeBilling(billing),
      ...(logo && { logo }),
    };

    createCounty.mutate(submitData);
  };

  const getSelectedCounty = () => {
    return counties.find((c: any) => c.id === formData.county_id);
  };

  const getSelectedModules = () => {
    return moduleTypes.filter((m: any) => formData.module_types.includes(m.id));
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // County Details
        return (
          <Box>
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                County Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select the county and provide basic details
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Logo Upload Section */}
              <Grid size={{ xs: 12 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: `2px dashed ${logoPreview ? colors.success.main : colors.grey[300]}`,
                    bgcolor: logoPreview ? `${colors.success.main}05` : colors.grey[50],
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: colors.primary.main,
                      bgcolor: `${colors.primary.main}05`,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 3 }}>
                    <Avatar
                      src={logoPreview}
                      sx={{
                        width: 120,
                        height: 120,
                        bgcolor: colors.primary.main,
                        boxShadow: logoPreview ? '0 8px 24px rgba(0,0,0,0.15)' : 'none',
                      }}
                    >
                      {!logoPreview && <Business sx={{ fontSize: 60 }} />}
                    </Avatar>
                    <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        County Logo / Seal
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Upload an official logo or seal for the county
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        Supported: PNG, JPG, SVG • Max size: 2MB
                      </Typography>
                      <Button
                        component="label"
                        variant={logoPreview ? 'outlined' : 'contained'}
                        startIcon={<CloudUpload />}
                        sx={{
                          borderRadius: 2,
                          px: 3,
                          ...(logoPreview && {
                            borderColor: colors.success.main,
                            color: colors.success.main,
                          }),
                        }}
                      >
                        {logoPreview ? 'Change Logo' : 'Upload Logo'}
                        <input type="file" hidden accept="image/*" onChange={handleLogoChange} />
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* County Selection */}
              <Grid size={{ xs: 12 }}>
                <FormControl
                  fullWidth
                  required
                  error={!!validationErrors.county_id}
                >
                  <InputLabel>County Name</InputLabel>
                  <Select
                    value={formData.county_id}
                    onChange={(e) => {
                      const countyId = e.target.value;
                      const county = counties.find((c: any) => c.id === countyId);
                      // Prefill the portal subdomain from the county name; keep any
                      // value the admin already typed by hand.
                      setFormData((prev) => ({
                        ...prev,
                        county_id: countyId as number | '',
                        subdomain:
                          prev.subdomain && prev.county_id !== ''
                            ? prev.subdomain
                            : county
                              ? slugifySubdomain(county.name)
                              : '',
                      }));
                    }}
                    label="County Name"
                    startAdornment={
                      <InputAdornment position="start">
                        <Business sx={{ color: colors.primary.main }} />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="">
                      <em>Select a County</em>
                    </MenuItem>
                    {counties.map((county: any) => (
                      <MenuItem key={county.id} value={county.id}>
                        {county.code} - {county.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {validationErrors.county_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {validationErrors.county_id[0]}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Portal subdomain — auto-suggested from the county name, editable */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Portal Subdomain"
                  name="subdomain"
                  value={formData.subdomain}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                    }))
                  }
                  placeholder="e.g., nairobi"
                  error={!!validationErrors.subdomain}
                  helperText={
                    validationErrors.subdomain?.[0] ||
                    (formData.subdomain
                      ? `Citizens will access the portal at https://${formData.subdomain}.${PORTAL_BASE_DOMAIN}`
                      : `Auto-generated from the county name — the county portal's web address`)
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Language sx={{ color: colors.primary.main }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">.{PORTAL_BASE_DOMAIN}</InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Office Location */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Office Location HQ"
                  name="office_location"
                  value={formData.office_location}
                  onChange={handleChange}
                  placeholder="e.g., County Hall, Main Street"
                  error={!!validationErrors.office_location}
                  helperText={validationErrors.office_location?.[0]}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn sx={{ color: colors.primary.main }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Website */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Official Website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://example.go.ke"
                  error={!!validationErrors.website}
                  helperText={validationErrors.website?.[0]}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Language sx={{ color: colors.primary.main }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1: // Contact Info
        return (
          <Box>
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Contact Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Provide official contact details for the county
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {contacts.length === 0 ? (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="info" icon={<ContactMail />}>
                    No contact types defined. You can skip this step and proceed to module selection.
                  </Alert>
                </Grid>
              ) : (
                contacts.map((contact: any) => {
                  const prefix = getContactPrefix(contact.name);
                  return (
                    <Grid key={contact.id} size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        label={contact.name}
                        value={formData.contacts[contact.id] || ''}
                        onChange={(e) => handleContactChangeWithPrefix(contact.id, e.target.value, contact.name)}
                        placeholder={getContactPlaceholder(contact.name)}
                        helperText={prefix ? `URL will be: ${prefix}${formData.contacts[contact.id] || '{handle}'}` : undefined}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {getContactIcon(contact.name)}
                            </InputAdornment>
                          ),
                          ...(prefix && {
                            sx: {
                              '& input': {
                                pl: 0.5,
                              },
                            },
                          }),
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        sx={{
                          '& .MuiInputBase-root': {
                            position: 'relative',
                          },
                          '& .MuiInputBase-input': prefix ? {
                            paddingLeft: '2px',
                          } : {},
                        }}
                      />
                      {prefix && formData.contacts[contact.id] && (
                        <Typography
                          variant="caption"
                          color="primary"
                          sx={{
                            display: 'block',
                            mt: 0.5,
                            fontWeight: 600,
                          }}
                        >
                          Full URL: {prefix}{formData.contacts[contact.id]}
                        </Typography>
                      )}
                    </Grid>
                  );
                })
              )}
            </Grid>
          </Box>
        );

      case 2: // Modules
        return (
          <Box>
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Select Modules
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose the functionality modules to enable for this county
              </Typography>
            </Box>

            <Grid container spacing={2.5}>
              {moduleTypes.map((moduleType: any) => {
                const isSelected = formData.module_types.includes(moduleType.id);
                return (
                  <Grid key={moduleType.id} size={{ xs: 12, sm: 6, md: 4 }}>
                    <Paper
                      elevation={isSelected ? 8 : 0}
                      sx={{
                        p: 2.5,
                        cursor: 'pointer',
                        border: `2px solid ${isSelected ? colors.primary.main : colors.grey[200]}`,
                        bgcolor: isSelected ? `${colors.primary.main}08` : 'white',
                        borderRadius: 2.5,
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          borderColor: colors.primary.main,
                          bgcolor: `${colors.primary.main}05`,
                          transform: 'translateY(-4px)',
                          boxShadow: `0 12px 24px ${colors.primary.main}20`,
                        },
                        '&::before': isSelected
                          ? {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              width: 0,
                              height: 0,
                              borderStyle: 'solid',
                              borderWidth: '0 60px 60px 0',
                              borderColor: `transparent ${colors.success.main} transparent transparent`,
                            }
                          : {},
                      }}
                      onClick={() => handleModuleToggle(moduleType.id)}
                    >
                      {isSelected && (
                        <CheckCircle
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            color: 'white',
                            fontSize: 20,
                            zIndex: 1,
                          }}
                        />
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: isSelected ? colors.primary.main : colors.grey[300],
                            width: 48,
                            height: 48,
                            transition: 'all 0.3s ease',
                          }}
                        >
                          <Extension />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                            {moduleType.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Enable {moduleType.name} services
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            {formData.module_types.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Alert severity="success" icon={<CheckCircle />}>
                  <Typography variant="body2" fontWeight={600}>
                    {formData.module_types.length} module{formData.module_types.length !== 1 ? 's' : ''} selected
                  </Typography>
                </Alert>
              </Box>
            )}
          </Box>
        );

      case 3: // Touchpoints
        return (
          <Box>
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Access Channels
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose how citizens will interact with this county. Each enabled channel gets an API key generated on submit.
              </Typography>
            </Box>

            <Grid container spacing={2.5}>
              {TOUCHPOINT_OPTIONS.map((opt) => {
                const isSelected = formData.touchpoints.includes(opt.key);
                return (
                  <Grid key={opt.key} size={{ xs: 12, sm: 6, md: 4 }}>
                    <Paper
                      elevation={isSelected ? 8 : 0}
                      sx={{
                        p: 2.5,
                        cursor: 'pointer',
                        border: `2px solid ${isSelected ? colors.primary.main : colors.grey[200]}`,
                        bgcolor: isSelected ? `${colors.primary.main}08` : 'white',
                        borderRadius: 2.5,
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          borderColor: colors.primary.main,
                          bgcolor: `${colors.primary.main}05`,
                          transform: 'translateY(-4px)',
                          boxShadow: `0 12px 24px ${colors.primary.main}20`,
                        },
                      }}
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          touchpoints: prev.touchpoints.includes(opt.key)
                            ? prev.touchpoints.filter((t) => t !== opt.key)
                            : [...prev.touchpoints, opt.key],
                        }));
                      }}
                    >
                      {isSelected && (
                        <CheckCircle
                          sx={{ position: 'absolute', top: 8, right: 8, color: colors.success.main, fontSize: 22, zIndex: 1 }}
                        />
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: isSelected ? colors.primary.main : colors.grey[300],
                            width: 48, height: 48, transition: 'all 0.3s ease',
                          }}
                        >
                          {opt.key === 'whatsapp' ? <Public /> : opt.key === 'mobile' ? <Phone /> : <Language />}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight={700} gutterBottom>{opt.title}</Typography>
                          <Typography variant="caption" color="text.secondary">{opt.description}</Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            {formData.touchpoints.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Alert severity="success" icon={<CheckCircle />}>
                  <Typography variant="body2" fontWeight={600}>
                    {formData.touchpoints.length} channel{formData.touchpoints.length !== 1 ? 's' : ''} enabled
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    API keys are shown once at the end of onboarding. You can regenerate them later from the county's edit page.
                  </Typography>
                </Alert>
              </Box>
            )}
          </Box>
        );

      case 4: // Admin Account
        return (
          <Box>
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Administrator Account
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create the primary admin account for this county
              </Typography>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              Login credentials will be automatically generated and sent to the admin's email address.
            </Alert>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="Enter first name"
                  error={!!validationErrors.first_name}
                  helperText={validationErrors.first_name?.[0]}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: colors.primary.main }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Enter last name"
                  error={!!validationErrors.last_name}
                  helperText={validationErrors.last_name?.[0]}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: colors.primary.main }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  type="email"
                  label="Email Address"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="admin@county.go.ke"
                  error={!!validationErrors.email}
                  helperText={validationErrors.email?.[0]}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: colors.primary.main }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+254 700 000 000"
                  error={!!validationErrors.phone}
                  helperText={validationErrors.phone?.[0]}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone sx={{ color: colors.primary.main }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 5: // Billing
        return (
          <Box>
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Billing Setup
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose how this county will be billed for using the platform
              </Typography>
            </Box>
            <BillingConfigForm value={billing} onChange={setBilling} />
          </Box>
        );

      case 6: // Review
        const selectedCounty = getSelectedCounty();
        const selectedModules = getSelectedModules();
        return (
          <Box>
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Review & Confirm
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please review all information before submitting
              </Typography>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 4,
                bgcolor: colors.grey[50],
                borderRadius: 3,
                border: `1px solid ${colors.grey[200]}`,
              }}
            >
              {/* County Info */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="overline" color="text.secondary" fontWeight={700}>
                  County Information
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      County
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {selectedCounty ? `${selectedCounty.code} - ${selectedCounty.name}` : 'Not selected'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Portal URL
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formData.subdomain
                        ? `https://${formData.subdomain}.${PORTAL_BASE_DOMAIN}`
                        : 'Auto-generated from county name'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Office Location
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formData.office_location || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Website
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formData.website || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Logo
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {logo ? logo.name : 'No logo uploaded'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Modules */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="overline" color="text.secondary" fontWeight={700}>
                  Enabled Modules ({selectedModules.length})
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {selectedModules.map((module: any) => (
                    <Chip
                      key={module.id}
                      label={module.name}
                      color="primary"
                      icon={<Extension />}
                      sx={{ borderRadius: 2 }}
                    />
                  ))}
                  {selectedModules.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No modules selected
                    </Typography>
                  )}
                </Stack>
              </Box>

              {/* Touchpoints */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="overline" color="text.secondary" fontWeight={700}>
                  Access Channels ({formData.touchpoints.length})
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {formData.touchpoints.map((tp) => (
                    <Chip
                      key={tp}
                      label={TOUCHPOINT_OPTIONS.find((o) => o.key === tp)?.title || tp}
                      color="success"
                      icon={tp === 'whatsapp' ? <Public /> : tp === 'mobile' ? <Phone /> : <Language />}
                      sx={{ borderRadius: 2 }}
                    />
                  ))}
                  {formData.touchpoints.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No channels selected
                    </Typography>
                  )}
                </Stack>
              </Box>

              {/* Billing */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="overline" color="text.secondary" fontWeight={700}>
                  Billing
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Type
                    </Typography>
                    <Typography variant="body1" fontWeight={600} textTransform="capitalize">
                      {billing.type.replace('_', ' ')}
                    </Typography>
                  </Grid>
                  {billing.type === 'subscription' && (
                    <>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Period
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {billing.starts_at || 'today'} → {billing.ends_at || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Amount
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {billing.subscription_currency} {billing.subscription_amount || 0}
                        </Typography>
                      </Grid>
                    </>
                  )}
                  {billing.type === 'service_fee' && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Fee
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {billing.service_fee_mode === 'percentage'
                          ? `${billing.service_fee_percentage || 0}% per transaction`
                          : `${billing.service_fee_tiers.length} tier(s)`}
                      </Typography>
                    </Grid>
                  )}
                  {billing.type === 'revenue_share' && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Split
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        Provider {billing.revenue_share_provider_percentage}% / County {billing.revenue_share_county_percentage}%
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Admin User */}
              <Box>
                <Typography variant="overline" color="text.secondary" fontWeight={700}>
                  Administrator Account
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Full Name
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formData.first_name} {formData.last_name}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Email
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formData.email}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Phone
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formData.phone}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            <Alert severity="warning" icon={<AdminPanelSettings />} sx={{ mt: 3 }}>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Important Notice
              </Typography>
              <Typography variant="body2">
                Once submitted, the county will be onboarded and the administrator will receive login
                credentials via email. Please ensure all information is accurate.
              </Typography>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="County Onboarding"
          subtitle="Follow the guided process to onboard a new county to the platform"
          breadcrumbs={[
            { label: 'Dashboard', path: '/superadmin/dashboard' },
            { label: 'Counties', path: '/superadmin/counties' },
            { label: 'Onboard County' },
          ]}
        />

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => {
              setError('');
              setValidationErrors({});
            }}
          >
            <Typography variant="body2" fontWeight={600} gutterBottom>
              {Object.keys(validationErrors).length > 0 ? 'Please fix the following errors:' : error}
            </Typography>
            {Object.keys(validationErrors).length > 0 && (
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                {Object.entries(validationErrors).map(([field, messages]) => (
                  <li key={field}>
                    <Typography variant="body2">
                      <strong>{field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}:</strong>{' '}
                      {(messages as string[]).join(', ')}
                    </Typography>
                  </li>
                ))}
              </Box>
            )}
          </Alert>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${colors.grey[200]}`,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
              p: 3,
              color: 'white',
            }}
          >
            <Stepper
              alternativeLabel
              activeStep={activeStep}
              connector={<ColorlibConnector />}
              sx={{
                '& .MuiStepLabel-label': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontWeight: 500,
                  '&.Mui-active': {
                    color: 'white',
                    fontWeight: 700,
                  },
                  '&.Mui-completed': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontWeight: 600,
                  },
                },
              }}
            >
              {steps.map((step) => (
                <Step key={step.label}>
                  <StepLabel StepIconComponent={ColorlibStepIcon}>{step.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Box sx={{ minHeight: 400 }}>{renderStepContent()}</Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 5,
                pt: 3,
                borderTop: `2px solid ${colors.grey[200]}`,
              }}
            >
              <Button
                variant="outlined"
                size="large"
                startIcon={<ArrowBack />}
                onClick={handleBack}
                disabled={activeStep === 0 || createCounty.isPending}
                sx={{ borderRadius: 2, px: 3 }}
              >
                Previous
              </Button>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Cancel />}
                  onClick={() => navigate('/superadmin/counties')}
                  disabled={createCounty.isPending}
                  sx={{ borderRadius: 2, px: 3 }}
                >
                  Cancel
                </Button>
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={createCounty.isPending ? <CircularProgress size={20} color="inherit" /> : <Save />}
                    onClick={handleSubmit}
                    disabled={createCounty.isPending}
                    sx={{
                      background: `linear-gradient(135deg, ${colors.success.main} 0%, ${colors.success.dark} 100%)`,
                      borderRadius: 2,
                      px: 4,
                      boxShadow: `0 4px 12px ${colors.success.main}40`,
                      '&:hover': {
                        boxShadow: `0 6px 16px ${colors.success.main}60`,
                      },
                    }}
                  >
                    {createCounty.isPending ? 'Creating County...' : 'Complete Onboarding'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForward />}
                    onClick={handleNext}
                    sx={{
                      background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
                      borderRadius: 2,
                      px: 4,
                      boxShadow: `0 4px 12px ${colors.primary.main}40`,
                      '&:hover': {
                        boxShadow: `0 6px 16px ${colors.primary.main}60`,
                      },
                    }}
                  >
                    Next Step
                  </Button>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </AdminLayout>
  );
};
